import React from 'react'

interface Props {
  open: boolean
  zoneName?: string
  saving?: boolean
  onClose: () => void
  onConfirm: () => void
}

// Confirm modal for zone deletion. Mirrors the legacy DeliveryZone dialog
// shape ("Delete zone" / "Are you sure you want to delete this zone?")
// while using the same overlay style as DeliveryZoneDialog so the two
// zone modals stay visually consistent.
export const DeleteZoneModal: React.FC<Props> = ({
  open,
  zoneName,
  saving = false,
  onClose,
  onConfirm,
}) => {
  if (!open) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.4)',
        padding: 16,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 380,
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 24px 48px rgba(0,0,0,0.18)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '20px 24px 4px' }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 10,
              background: '#FEF2F2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 14,
            }}
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#DC2626"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6" />
              <path d="M14 11v6" />
              <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
            </svg>
          </div>
          <h3
            style={{
              fontSize: 16,
              fontWeight: 600,
              color: '#2C3E50',
              margin: '0 0 6px',
            }}
          >
            Delete zone?
          </h3>
          <p
            style={{
              fontSize: 13,
              color: '#4B5563',
              margin: 0,
              lineHeight: 1.55,
            }}
          >
            {zoneName ? (
              <>
                <strong style={{ color: '#1F2937' }}>{zoneName}</strong> will be
                permanently removed. This can&rsquo;t be undone.
              </>
            ) : (
              <>
                Are you sure you want to delete this zone? This can&rsquo;t be
                undone.
              </>
            )}
          </p>
        </div>

        <div
          style={{
            padding: '16px 24px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 8,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            style={{
              padding: '8px 14px',
              fontSize: 12,
              fontWeight: 500,
              border: '1px solid #E8ECF0',
              borderRadius: 8,
              background: '#fff',
              color: '#2C3E50',
              cursor: saving ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={saving}
            style={{
              padding: '8px 16px',
              fontSize: 12,
              fontWeight: 500,
              border: '1px solid #DC2626',
              borderRadius: 8,
              background: '#DC2626',
              color: '#fff',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.7 : 1,
              fontFamily: 'inherit',
            }}
          >
            {saving ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  )
}
